import { useState, useCallback, useEffect, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Building2 } from 'lucide-react'
import { BuildingForm } from './BuildingForm'
import { BuildingResult } from './BuildingResult'
import { SavedBuildingsList } from './SavedBuildingsList'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useBuildingStore } from '../../../../stores/buildingStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import type { BuildingInput, BuildingResult as BuildingResultType, BuildingBatchResult } from '@creanizer/types'

export function BuildingGenerator() {
  const [activeTab, setActiveTab] = useState('generate')
  const [currentResult, setCurrentResult] = useState<BuildingResultType | null>(null)
  const [currentBatchResult, setCurrentBatchResult] = useState<BuildingBatchResult | null>(null)
  const [isSaved, setIsSaved] = useState(false)
  const [currentInput, setCurrentInput] = useState<BuildingInput | null>(null)
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  
  const generator = useGenerator('building')
  const streamGenerator = useStreamGenerator('building')
  const { 
    addBuilding, 
    addBatchBuildings, 
    updateBuilding,
    fixDuplicateIds,
    exportAsJSON,
    importBuildings,
    getStatistics 
  } = useBuildingStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { characters: savedCharacters } = useCharacterStore()
  const { savedCommunities } = useCommunityStore()
  const { savedSocieties } = useSocietyStore()
  // const { savedItems } = useItemStore() // TODO: ItemStore実装後に有効化
  const savedItems: any[] = []

  const handleGenerate = useCallback(async (input: BuildingInput) => {
    setError(null)
    setCurrentResult(null)
    setCurrentBatchResult(null)
    setIsSaved(false)
    setCurrentInput(input)
    
    try {
      // 世界観コンテキストの取得（全データを含む）
      if (input.worldContext) {
        const world = savedWorlds.find(w => w.id === input.worldContext)
        if (world) {
          input.worldContext = JSON.stringify(world.result)
        }
      }
      
      // 関連キャラクターコンテキストの取得（全データを含む）
      if (input.relatedCharacterContext) {
        const character = savedCharacters.find(c => c.id === input.relatedCharacterContext)
        if (character) {
          input.relatedCharacterContext = JSON.stringify(character.result)
        }
      }
      
      // 関連アイテムコンテキストの取得（全データを含む）
      if (input.relatedItemContext) {
        const item = savedItems.find(i => i.id === input.relatedItemContext)
        if (item) {
          input.relatedItemContext = JSON.stringify(item.result)
        }
      }
      
      // 関連コミュニティコンテキストの取得（全データを含む）
      if (input.relatedCommunityContext) {
        const community = savedCommunities.find(c => c.id === input.relatedCommunityContext)
        if (community) {
          input.relatedCommunityContext = JSON.stringify(community.result)
        }
      }
      
      // 関連ソサエティコンテキストの取得（全データを含む）
      if (input.relatedSocietyContext) {
        const society = savedSocieties.find(s => s.id === input.relatedSocietyContext)
        if (society) {
          input.relatedSocietyContext = JSON.stringify(society.result)
        }
      }
      
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              // バッチモードかチェック
              if (typeof resultData === 'object' && 'buildings' in resultData) {
                handleResult(resultData as BuildingBatchResult)
              } else if (typeof resultData === 'object' && ('basicInfo' in resultData || 'name' in resultData)) {
                handleResult(resultData as BuildingResultType)
              } else {
                console.error('Invalid result structure:', resultData)
                if (resultData) {
                  console.warn('Setting result despite structure issues')
                  handleResult(resultData as BuildingResultType | BuildingBatchResult)
                } else {
                  setError('結果のパースに失敗しました')
                }
              }
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            // ストリーミングエラーの場合は通常モードにフォールバック
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        if (response?.result) {
          handleResult(response.result as BuildingResultType | BuildingBatchResult)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '建造物生成中にエラーが発生しました')
    }
  }, [generator, streamGenerator, useStreaming, savedWorlds, savedCharacters, savedItems])
  
  const handleResult = useCallback((result: BuildingResultType | BuildingBatchResult) => {
    if ('buildings' in result) {
      // バッチモードの場合
      const batchResult = result as BuildingBatchResult
      setCurrentBatchResult(batchResult)
      setCurrentResult(batchResult.buildings[0])
    } else {
      // 通常モードの場合
      const buildingResult = result as BuildingResultType
      setCurrentResult(buildingResult)
    }
    
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }, [])

  const handleSave = useCallback(() => {
    if (!currentResult || !currentInput) return
    
    if (currentBatchResult) {
      // バッチモードの場合
      addBatchBuildings(
        currentBatchResult.buildings.map((building) => ({
          title: building.basicInfo.name,
          input: currentInput,
          result: building
        }))
      )
    } else {
      // 通常モードの場合
      addBuilding({
        title: currentResult.basicInfo.name,
        input: currentInput,
        result: currentResult
      })
    }
    
    setIsSaved(true)
  }, [currentResult, currentInput, currentBatchResult, addBuilding, addBatchBuildings])

  const handleEdit = useCallback((building: BuildingResultType) => {
    setCurrentResult(building)
    setIsSaved(true)
    setActiveTab('generate')
  }, [])
  
  const handleRenovation = useCallback(async (base: BuildingResultType, renovationInput: BuildingInput) => {
    const input: BuildingInput = {
      ...renovationInput,
      mode: 'renovation',
      renovationBase: base
    }
    
    await handleGenerate(input)
  }, [handleGenerate])
  
  const handleExport = useCallback(() => {
    exportAsJSON()
  }, [exportAsJSON])
  
  const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      try {
        const data = JSON.parse(content)
        importBuildings(data)
        setActiveTab('saved')
      } catch (error) {
        console.error('Import failed:', error)
        alert('インポートに失敗しました。有効なJSONファイルを選択してください。')
      }
    }
    reader.readAsText(file)
  }, [importBuildings])
  
  const handleFixDuplicates = useCallback(() => {
    const fixed = fixDuplicateIds()
    if (fixed) {
      console.log('Duplicate IDs fixed')
    }
  }, [fixDuplicateIds])
  
  useEffect(() => {
    // 初回マウント時に重複IDチェック
    handleFixDuplicates()
  }, [handleFixDuplicates])

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-5 w-5" />
          <h2 className="text-xl font-semibold">建造物ジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          城、神殿、塔、邸宅など、詳細な建造物を生成します
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({getStatistics().total}/500)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            <BuildingForm 
            onSubmit={handleGenerate}
            isGenerating={generator.isPending || streamGenerator.isStreaming}
            savedWorlds={savedWorlds}
            savedCharacters={savedCharacters}
            savedItems={[]}
            savedCommunities={savedCommunities}
            savedSocieties={savedSocieties}
            useStreaming={useStreaming}
            onStreamingChange={setUseStreaming}
            />
          </div>
          
          {streamGenerator.isStreaming && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <TabsContent value="saved">
          <SavedBuildingsList 
            onEdit={handleEdit}
            onBatchView={(batch) => {
              setCurrentBatchResult(batch)
              setCurrentResult(batch.buildings[0])
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            }}
            onExport={handleExport}
            onImport={handleImport}
          />
        </TabsContent>
      </Tabs>
      
      {currentResult && (
        <div className="card" ref={resultRef}>
          <BuildingResult 
            building={currentResult}
            batchResult={currentBatchResult}
            onRegenerate={(input) => handleGenerate(input)}
            onRenovate={(base, input) => handleRenovation(base, input)}
            onSwitchBuilding={(building) => setCurrentResult(building)}
            onSave={handleSave}
            isSaved={isSaved}
          />
        </div>
      )}
    </div>
  )
}